import type { MouseEvent } from "react";
import { LabLink } from "../chrome";
import {
  LAB_BRAND,
  LAB_CTA,
  LAB_EXERCISE,
  LAB_HEADLINE,
  LAB_LEAD,
  LAB_NAV_SIGNIN,
  LAB_NAV_TRAINERS,
  LAB_NAV_TUTORS,
  LAB_OTHER,
  LAB_PRICE,
  LAB_SECONDARY,
  LAB_SELLER,
  LAB_TRAINERS,
  LAB_TRUST,
} from "../copy";
import { useFanLesson } from "../lesson";
import "../../styles/lab-dawn.css";

type LabScreen = "landing" | "exercise";

export const DAWN_ID = "dawn" as const;
export const DAWN_NAME = "Утро";

type Traverse = (href: string, event: MouseEvent<HTMLAnchorElement>) => void;

function Head({
  basePath,
  hrefFor,
  onTraverse,
}: {
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <header className="dawn-head">
      <LabLink className="dawn-brand" href={hrefFor("landing")} onTraverse={onTraverse}>
        {LAB_BRAND}
      </LabLink>
      <nav className="dawn-nav" aria-label="Clever">
        <LabLink href={hrefFor("exercise")} onTraverse={onTraverse}>
          {LAB_NAV_TRAINERS}
        </LabLink>
        <span>{LAB_NAV_TUTORS}</span>
        <span>{LAB_NAV_SIGNIN}</span>
        <LabLink className="dawn-switch" href={basePath} onTraverse={onTraverse}>
          {LAB_OTHER}
        </LabLink>
      </nav>
    </header>
  );
}

export function DawnPoster(props: { href: string; onTraverse?: Traverse }) {
  return (
    <LabLink className="dawn-poster" href={props.href} onTraverse={props.onTraverse}>
      <div className="dawn-poster-wide" aria-hidden="true">
        <div className="dawn-poster-table">
          <span>🔊</span>
          <span>🎬</span>
          <span>🎭</span>
        </div>
        <p className="dawn-poster-brand">{LAB_BRAND}</p>
        <p className="dawn-poster-h">
          Три тренажёра —
          <br />
          один живой английский
        </p>
        <span className="dawn-poster-cta">{LAB_CTA}</span>
      </div>
      <div className="dawn-poster-slim" aria-hidden="true">
        <div>
          <p className="dawn-poster-brand">{LAB_BRAND}</p>
          <p className="dawn-poster-h">
            Три тренажёра —
            <br />
            один живой английский
          </p>
          <span className="dawn-poster-cta">{LAB_CTA}</span>
        </div>
        <p className="dawn-poster-marks">
          <span>🔊</span>
          <span>🎬</span>
          <span>🎭</span>
        </p>
      </div>
      <span className="sr-only">
        {DAWN_NAME}. {LAB_HEADLINE}
      </span>
    </LabLink>
  );
}

export function DawnWorld(props: {
  screen: LabScreen;
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <div data-world={DAWN_ID} data-lab-screen={props.screen} className="dawn-world">
      <Head basePath={props.basePath} hrefFor={props.hrefFor} onTraverse={props.onTraverse} />
      {props.screen === "exercise" ? (
        <DawnExercise />
      ) : (
        <DawnLanding hrefFor={props.hrefFor} onTraverse={props.onTraverse} />
      )}
    </div>
  );
}

function DawnLanding({
  hrefFor,
  onTraverse,
}: {
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <main className="dawn-landing">
      <section className="dawn-hero">
        <div className="dawn-copy">
          <h1>
            Три тренажёра —
            <br />
            один живой английский
          </h1>
          <p className="dawn-lead">{LAB_LEAD}</p>
          <div className="dawn-actions">
            <LabLink className="dawn-cta" href={hrefFor("exercise")} onTraverse={onTraverse}>
              {LAB_CTA}
            </LabLink>
            <LabLink className="dawn-text" href={hrefFor("exercise")} onTraverse={onTraverse}>
              {LAB_SECONDARY}
            </LabLink>
          </div>
          <p className="dawn-trust">{LAB_TRUST}</p>
        </div>
        <aside className="dawn-still" aria-hidden="true">
          <div className="dawn-table">
            <span className="dawn-mark dawn-mark-sound">🔊</span>
            <span className="dawn-mark dawn-mark-word">🎬</span>
            <span className="dawn-mark dawn-mark-phrase">🎭</span>
          </div>
          <p>звуки · слова · фразы</p>
        </aside>
      </section>
      <ul className="dawn-trainers">
        {LAB_TRAINERS.map((trainer) => (
          <li key={trainer.title}>
            <LabLink href={hrefFor("exercise")} onTraverse={onTraverse}>
              <span className="dawn-trainer-emoji" aria-hidden="true">
                {trainer.emoji}
              </span>
              <span>
                <strong>{trainer.title}</strong>
                <em>{trainer.goal}</em>
                <small>{trainer.meta}</small>
              </span>
            </LabLink>
          </li>
        ))}
      </ul>
      <footer className="dawn-foot">
        <p>{LAB_SELLER.name}</p>
        <p>{LAB_PRICE}</p>
      </footer>
    </main>
  );
}

function DawnExercise() {
  const lesson = useFanLesson();
  return (
    <main className="dawn-lesson" data-play={lesson.play}>
      <div className="dawn-lesson-copy">
        <p className="dawn-kicker">{LAB_EXERCISE.kicker}</p>
        <h1>{LAB_EXERCISE.title}</h1>
        <p className="dawn-lead">{LAB_EXERCISE.lead}</p>
        <p className="dawn-status" role="status">
          {LAB_EXERCISE.status[lesson.play]}
        </p>
        <div className="dawn-tools">
          <button type="button" className={lesson.play === "listen" ? "dawn-cta" : "dawn-ghost"} onClick={lesson.listen}>
            {LAB_EXERCISE.listen}
          </button>
          <button type="button" className={lesson.play === "speak" ? "dawn-cta" : "dawn-ghost"} onClick={lesson.speak}>
            {LAB_EXERCISE.speak}
          </button>
          <button
            type="button"
            className={lesson.play === "done" ? "dawn-cta" : "dawn-ghost"}
            disabled={!lesson.ready}
            onClick={lesson.finish}
          >
            {LAB_EXERCISE.done}
          </button>
        </div>
      </div>
      <div className="dawn-wordcard">
        <span aria-hidden="true">💨</span>
        <strong>{LAB_EXERCISE.word}</strong>
        <em>{LAB_EXERCISE.gloss}</em>
        <p>{LAB_EXERCISE.example}</p>
      </div>
    </main>
  );
}
